import type { OutletResource } from '@repo/api-client';
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { useMemo } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { DayPicker } from '../back-office/DayPicker';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';

/**
 * The two fields registering and moving a machine share: which outlet, and from
 * which day. Both writes send the same pair, so they are written once.
 */

/** The picker's "attached to nothing" — the attach route's `outletId: null`. */
export const NO_OUTLET = 'NONE';

export function OutletField<T extends FieldValues>({
  control,
  name,
  outlets,
  idPrefix,
  allowNothing = false,
}: {
  control: Control<T>;
  name: Path<T>;
  /** The outlets still trading. A retired one is refused, so it is not offered. */
  outlets: readonly OutletResource[];
  idPrefix: string;
  /** Offer "Attached to nothing", which detaches the machine. */
  allowNothing?: boolean;
}) {
  const items = useMemo(
    () => [
      ...(allowNothing ? [{ label: 'Attached to nothing', value: NO_OUTLET }] : []),
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets, allowNothing],
  );

  const id = `${idPrefix}-outlet`;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel required className={fieldLabel}>
            Outlet
          </FormLabel>
          {/* Not in `FormControl`: the trigger is a button, so it is described
              by the message by hand, as the terminal form does. */}
          <Select
            value={field.value as string}
            items={items}
            onValueChange={(next) => field.onChange(next ?? '')}
            disabled={items.length === 0}
          >
            <SelectTrigger
              id={id}
              aria-label="Outlet"
              aria-invalid={fieldState.error !== undefined}
              aria-describedby={fieldState.error === undefined ? undefined : `${id}-error`}
              className={`${fieldSurface} h-11 w-full`}
            >
              <SelectValue placeholder="Choose an outlet" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {items.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FormMessage id={`${id}-error`} />
        </FormItem>
      )}
    />
  );
}

export function DayField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  maxDay,
  idPrefix,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  description: string;
  /**
   * The latest day offered — today's business date for a write that refuses a
   * later one. Absent where a future day is fine (a revenue-share period).
   */
  maxDay?: string;
  idPrefix: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel required className={fieldLabel} htmlFor={`${idPrefix}-day`}>
            {label}
          </FormLabel>
          <DayPicker
            id={`${idPrefix}-day`}
            value={field.value as string}
            emptyLabel="Choose a day"
            format={formatBusinessDate}
            fromDate={fromCalendarDate}
            toDate={toCalendarDate}
            maxDay={maxDay}
            onValueChange={field.onChange}
          />
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
